import { datadogRum } from '@datadog/browser-rum';
import { useMemo, useState } from 'react';
import { ProductFruits } from 'react-product-fruits';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect, useLocation } from 'react-use';

import { APIConfiguration } from '@savgroup-front-common/configuration';
import { useGetEnabledFeatureFlagsQuery } from '@savgroup-front-common/core/api/Authorization/useGetEnabledFeatureFlagsQuery';
import { useNewUiContext } from '@savgroup-front-common/core/components/NewUiProvider/NewUiProvider';
import { selectLocale } from '@savgroup-front-common/core/domains/i18n/selectors';
import {
  useCurrentUser,
  useLoginStore,
} from '@savgroup-front-common/core/domains/login/useLoginStore';
import {
  buildErrorNotification,
  getUniqErrorMessages,
} from '@savgroup-front-common/core/helpers';
import { signinSilent } from '@savgroup-front-common/core/services/userManager';

import { logError } from '../../../configuration';
import { PRODUCT_FRUITS_CONFIG } from '../../../configuration/productFruits';
import { signIn } from '../../../domains/login/actions';
import { useAppContext } from '../App.context';
import Layout from '../layout/Layout';

import { useIsCypressRunning } from './CheckAuthorized.hooks';
import LoadingScreen from './LoadingScreen';
import { NotRegisteredUser } from './NotRegisteredUser/NotRegisteredUser';

const ACCESS_TOKEN_REFRESH_THRESHOLD_IN_SECONDS = 300;

export const OidcCheckAuthorized = () => {
  const locale = useSelector(selectLocale);
  const { pathname, search } = useLocation();
  const invitationId = new URLSearchParams(search).get('invitation_id');
  const invitationToken = new URLSearchParams(search).get('invitation_token');
  const authenticatedUser = useCurrentUser();
  const setLoginStore = useLoginStore((state) => state.setLoginStore);
  const errors = authenticatedUser?.errors;

  const { isDarkModeEnabled } = useNewUiContext();
  const { user, isAuthenticated, login, selectedSeller } = useAppContext();
  const [isNotRegisteredUser, setIsNotRegisteredUser] = useState(false);
  const sellerId = selectedSeller?.id;
  const dispatch = useDispatch();
  const isCypressRunning = useIsCypressRunning();
  const featureFlags = useGetEnabledFeatureFlagsQuery({
    sellerId: sellerId ?? '',
    enabled: isAuthenticated && !!sellerId,
  });

  const userInfo = useMemo(
    () => ({
      username: authenticatedUser?.mail,
      email: authenticatedUser?.mail,
      firstname: authenticatedUser?.firstname,
      lastname: authenticatedUser?.lastname,
      props: {
        roles: authenticatedUser?.roles,
        featureFlags,
      },
    }),
    [
      authenticatedUser?.mail,
      authenticatedUser?.firstname,
      authenticatedUser?.lastname,
      authenticatedUser?.roles,
      featureFlags,
    ],
  );

  useDeepCompareEffect(() => {
    (async () => {
      if (user && isAuthenticated) {
        if (
          (user.expires_in ?? 0) < ACCESS_TOKEN_REFRESH_THRESHOLD_IN_SECONDS
        ) {
          try {
            await signinSilent({ resource: APIConfiguration.apiBase });
          } catch (error) {
            logError('Error when trying to signinSilent (refresh token): ', {
              error,
            });

            await login();
          }

          return;
        }

        if (!isNotRegisteredUser && !authenticatedUser?.isLoaded) {
          dispatch(
            signIn({
              onGetUserForbidden: () => setIsNotRegisteredUser(true),
              setLoginStore,
            }) as any,
          );
        }

        return;
      }

      login({
        extraQueryParams:
          invitationId && invitationToken
            ? {
                invitation_id: invitationId,
                invitation_token: invitationToken,
              }
            : undefined,
        returnTo: `${pathname}${search}`,
      });
    })();
  }, [
    dispatch,
    invitationId,
    invitationToken,
    isAuthenticated,
    isNotRegisteredUser,
    login,
    pathname,
    search,
    user,
    authenticatedUser,
    setLoginStore,
  ]);

  const notifications = getUniqErrorMessages(errors || []).map(
    buildErrorNotification,
  );

  if (isNotRegisteredUser) {
    return <NotRegisteredUser />;
  }

  if (!user || !isAuthenticated || !authenticatedUser?.isLoaded) {
    return <LoadingScreen notifications={notifications} />;
  }

  datadogRum.setUser({
    id: authenticatedUser.userId,
    name: `${authenticatedUser.firstname} ${authenticatedUser.lastname}`,
    email: authenticatedUser.mail,
    accesses: authenticatedUser.accesses,
    permissions: authenticatedUser.permissions,
    isDarkModeEnabled,
  });

  return (
    <>
      <Layout />

      {!isCypressRunning && PRODUCT_FRUITS_CONFIG.workspaceCode && (
        <ProductFruits
          workspaceCode={PRODUCT_FRUITS_CONFIG.workspaceCode}
          language={locale}
          user={userInfo as any}
        />
      )}
    </>
  );
};
